import storeAndPersist from 'store';
import {
  extractNonHistoricalTicketIdFromState,
  getSimplrAccountIdOfSelectedTicket,
  getSelectedTicketBrand,
} from 'extractFromState';
import { logout as logoutExpert } from 'api/user';
import * as LogAdapter from './LogAdapter';
import {
  defaultErrorPopupMessage,
  ERROR_MESSAGES,
  HANDLE_ERROR_INVALID_ARGS,
  logoutPopupTimeout,
  LOG_MESSAGE_INVALID_ARGS,
} from './constants';
import SimplrErrorBoundary from './ErrorBoundary/ErrorBoundary';
import { showErrorPopup } from 'components/popups/ErrorPopup/ErrorPopup';
import { processErrorMessage } from './errorMessageProcessing';
import * as ErrorService from './ErrorService';
import HttpStatus, { StatusCodes } from 'http-status-codes';
import * as SimplrErrors from '@asurion-core/typescript-utils/SimplrErrors';
import AuthService from 'services/authService';

export const ErrorBoundary = SimplrErrorBoundary;

const getSessionExpiryContext = () => {
  const current = AuthService.current();
  const sessionExpiresAtMs = current?.sessionExpiresAtMs;
  const remainingMs = sessionExpiresAtMs ? sessionExpiresAtMs - Date.now() : null;

  return {
    sessionExpiresAtMs,
    sessionRemainingMs: remainingMs,
    sessionStillValid: remainingMs > 0,
  };
};

export const handleUnauthorizedError = (context = {}) => {
  LogAdapter.logError({
    error: new Error('[SimplrWeb] Logout triggered: unauthorized error'),
    customMessage: '[SimplrWeb] Logout triggered: unauthorized error',
    customFields: { ...context, ...getSessionExpiryContext() },
  });
  showErrorPopup(ERROR_MESSAGES.SESSION_IS_INVALID);
  setTimeout(async () => {
    await logoutExpert();
  }, logoutPopupTimeout);
};

export const handleError = ({
  error,
  customMessage,
  customFields,
  showPopup = false,
  popupOnly = false,
  logout = false,
}) => {
  if (!customMessage && !error && !popupOnly) {
    throw new Error(HANDLE_ERROR_INVALID_ARGS);
  }

  // if the provided error has a mapped default message we should override any custom message
  const defaultMessage = processErrorMessage(error, customMessage);
  let processedMessage = defaultMessage || customMessage;

  if (typeof error === 'string') {
    let manualError = new Error(error);
    LogAdapter.logError({ error: manualError });
  } else if (!error && processedMessage) {
    LogAdapter.logMessage(processedMessage);
  } else if (!popupOnly) {
    LogAdapter.logError({
      customMessage: processedMessage,
      customFields: addStandardCustomFields(customFields),
      error,
    });
  }

  const isAuthErrorFromService = ErrorService.isAuthError(error);
  const isSimplrAuthError = error?.code === SimplrErrors.Codes.AUTHENTICATION_ERROR;
  const isHttpUnauthorized = error?.code === HttpStatus.UNAUTHORIZED;
  const isAuthError = logout || isAuthErrorFromService || isSimplrAuthError || isHttpUnauthorized;

  if (isAuthError) {
    LogAdapter.logError({
      error: new Error('[SimplrWeb] Auth error detected'),
      customMessage: '[SimplrWeb] Auth error detected',
      customFields: {
        logoutFlag: logout,
        isAuthErrorFromService,
        isSimplrAuthError,
        isHttpUnauthorized,
        errorCode: error?.code,
        errorStatus: error?.status,
        errorMessage: error?.message,
      },
    });
  }

  if (isAuthError) {
    handleUnauthorizedError({
      source: 'handleError',
      errorCode: error?.code,
      errorStatus: error?.status,
      responseCode: error?.response?.body?.code ?? error?.response?.code,
      customMessage,
      logoutFlag: logout,
    });
  } else if (showPopup || popupOnly) {
    const errorMessage = processedMessage || defaultErrorPopupMessage;
    showErrorPopup(errorMessage);
  }
};

export const logMessage = (message) => {
  if (typeof message !== 'string') {
    throw new Error(LOG_MESSAGE_INVALID_ARGS);
  }

  LogAdapter.logMessage(message);
};

export const addStandardCustomFields = (customFields) => {
  let errorFields = customFields || {};

  // uses an emergency back up object in case for some reason storeAndPersist does not work. Also used in unit tests.
  const { store } = storeAndPersist || { store: { getState: () => ({}) } };
  const state = store.getState();

  let standardCustomFieldsMap = {
    simplrAccountId: () => getSimplrAccountIdOfSelectedTicket(state),
    selectedTicketId: () => extractNonHistoricalTicketIdFromState(state),
    brand: () => getSelectedTicketBrand(state),
  };

  for (let field of Object.keys(standardCustomFieldsMap)) {
    if (!Object.keys(errorFields).includes(field)) {
      errorFields[field] = standardCustomFieldsMap[field]();
    }
  }

  return errorFields;
};

export const init = LogAdapter.init;
export const configure = LogAdapter.configure;

export const isAuthError = (error) => {
  if (error?.status === HttpStatus.UNAUTHORIZED && error?.response?.body?.code === HttpStatus.UNAUTHORIZED) {
    return true;
  }

  const responseError = error?.response?.body ?? error?.response;

  return (
    SimplrErrors.SimplrError.isSimplrError(responseError) &&
    responseError.code === SimplrErrors.Codes.AUTHENTICATION_ERROR
  );
};

export * as ErrorConstants from './constants';

export const handleGraphQLErrors = ({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    graphQLErrors.map(({ message, locations, path }) =>
      LogAdapter.logError({ error: `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}` })
    );
  }

  if (networkError) {
    LogAdapter.logError({ error: `[Network error]: ${networkError}` });
  }

  if (networkError?.statusCode === StatusCodes.UNAUTHORIZED) {
    handleUnauthorizedError({
      source: 'handleGraphQLErrors',
      statusCode: networkError.statusCode,
      networkErrorMessage: networkError.message,
    });
  }
};
